/**
 * Investidores — Central de Investidores (flat file, self-contained)
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp, Landmark, FileText, Download,
  Target, Users, Cpu, Globe, DollarSign, BarChart3, Layers,
  CheckCircle2, Clock, ChevronRight, Briefcase, Shield, Rocket,
  Receipt, Banknote, RefreshCw, Video,
  MessageSquare, Bot, Pencil, X, Save, Plus, Send
} from 'lucide-react';
import { BASELINE_SCENARIO_KEY, getScenarioDisplayName } from '../../constants/governance';
import { 
  fetchDREForScenario, 
  fetchCashflowForScenario, 
  fetchInvestorSummary,
  fetchSwimVisionDRE,
  fetchSwimVisionCashflow,
  fetchSwimVisionPartnerView
} from '../../services/governanceScenariosService';
import DataSourceBadge from '../../components/governance/DataSourceBadge';
import { VALUATION_METHODS, CURRENT_VALUATION, CAP_TABLE, USE_OF_FUNDS, getAllValuationScenarios } from '../../data/finance/valuation';
import { INVESTOR_THESIS, MARKET_NARRATIVE } from '../../data/investors/narrative';
import { DUE_DILIGENCE_SECTIONS } from '../../data/investors/dueDiligence';
import { FINANCIAL_SCENARIOS, INVESTMENT_SCENARIOS } from '../governance/commercial/financialScenariosData';
import { FINANCIAL_ASSUMPTIONS } from '../../data/finance/assumptions';
import { R } from '../../navigation/opsRoutes';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const INVESTORS_LABELS = {
  pt: {
    tabs: {
      overview: 'Visão Geral', dre: 'DRE Projetada', cashflow: 'Fluxo de Caixa', produtos: 'Produtos',
      'cap-table': 'Cap Table', thesis: 'Tese de Dados & IA', market: 'Mercado',
      'use-of-funds': 'Uso dos Recursos', financial: 'Financeiro', valuation: 'Valuation', 'due-diligence': 'Due Diligence',
    },
    title: 'Investidores',
    subtitle: 'Data Room, documentos e materiais para captação Seed.',
    scenarioLabel: 'Cenário de Análise',
    agentBtn: 'Vukz Agent',
  },
  en: {
    tabs: {
      overview: 'Overview', dre: 'Projected P&L', cashflow: 'Cash Flow', produtos: 'Products',
      'cap-table': 'Cap Table', thesis: 'Data & AI Thesis', market: 'Market',
      'use-of-funds': 'Use of Funds', financial: 'Financial', valuation: 'Valuation', 'due-diligence': 'Due Diligence',
    },
    title: 'Investors',
    subtitle: 'Data Room, documents and materials for Seed fundraising.',
    scenarioLabel: 'Analysis Scenario',
    agentBtn: 'Vukz Agent',
  }
};

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

const TABS = [
  { id: 'overview', icon: Layers },
  { id: 'dre', icon: Receipt },
  { id: 'cashflow', icon: Banknote },
  { id: 'produtos', icon: Cpu },
  { id: 'cap-table', icon: Users },
  { id: 'thesis', icon: Cpu },
  { id: 'market', icon: Globe },
  { id: 'use-of-funds', icon: DollarSign },
  { id: 'financial', icon: BarChart3 },
  { id: 'valuation', icon: TrendingUp },
  { id: 'due-diligence', icon: Shield },
];

const OverviewTab = ({ scenarioKey, investorSummary, summarySource, onNavigate }) => {
  const [svData, setSvData] = useState(null);
  const [svLoading, setSvLoading] = useState(false);

  // Derive scenario from scenarioKey
  const scenario = useMemo(() =>
    (FINANCIAL_SCENARIOS || []).find(s => s.id === scenarioKey) ||
    (FINANCIAL_SCENARIOS || [])[0],
    [scenarioKey]
  );

  // Derive DRE rows inline (same logic as buildDRERows defined below)
  const dreRows = useMemo(() => {
    if (!scenario) return [];
    const tax = FINANCIAL_ASSUMPTIONS?.taxOnRevenue ?? 0.0933;
    return (scenario.yearly || []).map(y => {
      const rec_bruta = y.arr;
      const impostos = Math.round(rec_bruta * tax);
      const rec_liquida = rec_bruta - impostos;
      const custo_total = y.costs * 12;
      const cogs = Math.round(custo_total * 0.35);
      const lucro_bruto = rec_liquida - cogs;
      const opex = Math.round(custo_total * 0.65);
      const ebitda = lucro_bruto - opex;
      return { year: y.year, rec_bruta, impostos, rec_liquida, cogs, lucro_bruto, opex, ebitda, clientes: y.clients };
    });
  }, [scenario]);

  // Derive Cashflow rows inline
  const cfRows = useMemo(() => {
    if (!scenario) return [];
    const inv = INVESTMENT_SCENARIOS?.[scenario.investment] || {};
    let saldo = inv.amount || 0;
    return (scenario.yearly || []).map(y => {
      const recebimentos = Math.round(y.arr * 0.97);
      const custo_total = y.costs * 12;
      const folha  = Math.round(custo_total * 0.50);
      const azure  = Math.round(custo_total * 0.20);
      const mktg   = Math.round(custo_total * 0.15);
      const adm    = Math.round(custo_total * 0.15);
      const total_saidas = folha + azure + mktg + adm;
      const variacao = recebimentos - total_saidas;
      saldo = saldo + variacao;
      const burn_mensal = Math.round(total_saidas / 12);
      const runway = burn_mensal > 0 ? Math.round(saldo / burn_mensal) : null;
      return { year: y.year, recebimentos, total_saidas, variacao, saldo_fim: saldo, burn_mensal, runway };
    });
  }, [scenario]);

  // Fetch SwimVision live on scenario change
  useEffect(() => {
    setSvLoading(true);
    fetchSwimVisionDRE(scenarioKey, 12).then(result => {
      setSvData(result.data);
      setSvLoading(false);
    });
  }, [scenarioKey]);

  // Derived KPIs
  const y1 = dreRows[0];
  const y3 = dreRows.find(r => r.year === (dreRows[0]?.year + 2)) || dreRows[dreRows.length - 1];
  const cf1 = cfRows[0];
  const breakEvenRow = dreRows.find(r => r.ebitda >= 0);
  const inv = scenario ? (INVESTMENT_SCENARIOS?.[scenario.investment] || {}) : {};

  // Due Diligence progress
  const totalDD = DUE_DILIGENCE_SECTIONS.reduce((a, s) => a + s.items.length, 0);
  const doneDD  = DUE_DILIGENCE_SECTIONS.reduce((a, s) => a + s.items.filter(i => i.status === 'done').length, 0);
  const ddPct   = totalDD > 0 ? Math.round((doneDD / totalDD) * 100) : 0;

  const fmtK = (n) => {
    if (n == null) return '—';
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1e6) return `${sign}R$ ${(abs / 1e6).toFixed(1)}M`;
    return `${sign}R$ ${(abs / 1e3).toFixed(0)}K`;
  };

  // Subcomponents
  const KpiCard = ({ label, value, sub, color, tabId }) => (
    <div
      onClick={() => tabId && onNavigate?.(tabId)}
      className={`bg-white border border-gray-200 rounded-xl p-4 text-center ${
        tabId ? 'cursor-pointer hover:shadow-md hover:border-gray-300 transition-all' : ''
      }`}
    >
      <div className="text-xl font-black leading-tight" style={{ color: color || BRAND_GREEN }}>{value}</div>
      <div className="text-xs font-semibold text-gray-700 mt-0.5">{label}</div>
      {sub && <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );

  const SectionCard = ({ title, icon: Icon, iconColor, tabId, badge, children }) => (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4" style={{ color: iconColor || BRAND_GREEN }} />}
          <span className="font-bold text-sm text-gray-900">{title}</span>
          {badge && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-green-100 text-green-700">
              {badge}
            </span>
          )}
        </div>
        {tabId && (
          <button
            onClick={() => onNavigate?.(tabId)}
            className="text-xs font-semibold flex items-center gap-0.5 hover:underline"
            style={{ color: BRAND_GREEN }}
          >
            Ver completo <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );

  const MiniTable = ({ cols, rows }) => (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b">
          <th className="text-left py-1 text-gray-400">Linha</th>
          {cols.map(c => <th key={c} className="text-right py-1 text-gray-400">{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.label} className="border-b last:border-0">
            <td className={`py-1 text-gray-600 ${row.bold ? 'font-bold' : ''}`}>{row.label}</td>
            {row.values.map((v, i) => (
              <td key={i} className={`text-right py-1 ${row.bold ? 'font-bold' : ''}`}
                style={{ color: v < 0 ? '#dc2626' : (row.color || '#374151') }}>
                {row.fmt ? row.fmt(v) : fmtK(v)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-gray-900">
          Visão Geral —{' '}
          <span style={{ color: BRAND_GREEN }}>{scenario?.label || scenarioKey}</span>
        </h3>
        <DataSourceBadge source={summarySource} />
      </div>

      {/* ── KPI Strip ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard
          label="Receita Ano 1" tabId="dre"
          value={fmtK(y1?.rec_bruta)}
          sub={y1 ? `${y1.clientes} clientes` : '—'}
        />
        <KpiCard
          label="Receita Ano 3" tabId="dre"
          value={fmtK(y3?.rec_bruta)}
          sub={y3 ? String(y3.year) : '—'}
        />
        <KpiCard
          label="EBITDA Ano 3" tabId="dre"
          value={fmtK(y3?.ebitda)}
          color={y3?.ebitda != null ? (y3.ebitda >= 0 ? BRAND_GREEN : '#dc2626') : undefined}
          sub="resultado operacional"
        />
        <KpiCard
          label="Burn Rate" tabId="cashflow"
          value={cf1 ? `R$${(cf1.burn_mensal / 1e3).toFixed(0)}K/m` : '—'}
          color="#dc2626"
          sub="mensal, Ano 1"
        />
        <KpiCard
          label="Runway" tabId="cashflow"
          value={cf1?.runway != null ? `${cf1.runway} meses` : '—'}
          color={cf1?.runway != null && cf1.runway < 12 ? '#dc2626' : BRAND_GREEN}
          sub={inv.amount > 0 ? `c/ R$${(inv.amount / 1e3).toFixed(0)}K` : 'bootstrap'}
        />
        <KpiCard
          label="Break-even" tabId="dre"
          value={breakEvenRow ? String(breakEvenRow.year) : 'Além Y3'}
          color={breakEvenRow ? BRAND_GREEN : BRAND_GOLD}
          sub="1º ano EBITDA ≥ 0"
        />
      </div>

      {/* ── Row: DRE + Cashflow + SwimVision ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title="DRE Projetada" icon={Receipt} tabId="dre">
          <MiniTable
            cols={dreRows.map(r => String(r.year))}
            rows={[
              { label: 'Receita Bruta', values: dreRows.map(r => r.rec_bruta), color: BRAND_GREEN },
              { label: 'Lucro Bruto',   values: dreRows.map(r => r.lucro_bruto), color: BRAND_GREEN },
              { label: 'EBITDA',        values: dreRows.map(r => r.ebitda), bold: true },
              { label: 'Clientes',      values: dreRows.map(r => r.clientes), color: '#374151', fmt: v => v },
            ]}
          />
        </SectionCard>

        <SectionCard title="Fluxo de Caixa" icon={Banknote} tabId="cashflow">
          <MiniTable
            cols={cfRows.map(r => String(r.year))}
            rows={[
              { label: 'Recebimentos', values: cfRows.map(r => r.recebimentos), color: BRAND_GREEN },
              { label: 'Desembolsos',  values: cfRows.map(r => -r.total_saidas), color: '#dc2626' },
              { label: 'Saldo Final',  values: cfRows.map(r => r.saldo_fim), bold: true },
              { label: 'Runway',       values: cfRows.map(r => r.runway),
                fmt: v => v != null ? `${v}m` : '—',
                color: BRAND_GREEN },
            ]}
          />
        </SectionCard>

        <SectionCard title="Swim Vision" icon={Video} iconColor="#0284c7" tabId="swimvision" badge="Live">
          {svLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-5 h-5 animate-spin text-gray-300" />
            </div>
          ) : svData?.rows?.length > 0 ? (
            <div className="space-y-3">
              {svData.meta && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-blue-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-blue-900">{svData.meta.clubes_ativos ?? '—'}</div>
                    <div className="text-[10px] text-blue-600">Clubes ativos</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-blue-900">{fmtK(svData.meta.total_receita_bruta)}</div>
                    <div className="text-[10px] text-blue-600">Receita 12m</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold" style={{ color: BRAND_GREEN }}>
                      {svData.meta.margem_pct != null ? `${(svData.meta.margem_pct * 100).toFixed(0)}%` : '—'}
                    </div>
                    <div className="text-[10px] text-blue-600">Margem bruta</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-2 text-center">
                    <DataSourceBadge source={svData.source || 'mock'} />
                  </div>
                </div>
              )}
              <table className="w-full text-xs">
                <tbody>
                  {(svData.rows || []).slice(0, 5).map((row, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1 text-gray-500">{row.label || row.mes || `Mês ${i + 1}`}</td>
                      <td className="text-right py-1 font-medium"
                        style={{ color: (row.margem_bruta ?? row.receita_bruta ?? 0) < 0 ? '#dc2626' : BRAND_GREEN }}>
                        {fmtK(row.margem_bruta ?? row.receita_bruta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6 text-xs text-gray-400">
              <Video className="w-7 h-7 mx-auto mb-2 text-gray-300" />
              Aguardando dados do backend
            </div>
          )}
        </SectionCard>
      </div>

      {/* ── Row: Valuation + Cap Table + Due Diligence ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title="Valuation" icon={TrendingUp} tabId="valuation">
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-gray-500">Pre-Money</span>
              <span className="text-xl font-extrabold" style={{ color: BRAND_GREEN }}>
                R$ {(CURRENT_VALUATION.preMoneyBRL / 1e6).toFixed(1)}M
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-gray-500">USD</span>
              <span className="text-sm font-semibold" style={{ color: BRAND_GOLD }}>
                USD {(CURRENT_VALUATION.preMoneyUSD / 1e6).toFixed(2)}M
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-gray-500">Estágio</span>
              <span className="text-xs font-medium text-gray-700">{CURRENT_VALUATION.stage}</span>
            </div>
            <div className="border-t pt-2 mt-1 space-y-1">
              <div className="text-[10px] text-gray-400 mb-1">Por método:</div>
              {VALUATION_METHODS.slice(0, 3).map(m => (
                <div key={m.id} className="flex justify-between text-xs py-0.5">
                  <span className="text-gray-600 truncate">{m.name}</span>
                  <span className="font-semibold ml-2 shrink-0" style={{ color: BRAND_GREEN }}>
                    {m.value
                      ? `R$ ${(m.value / 1e6).toFixed(1)}M`
                      : m.scenarios
                        ? `${(m.scenarios[0].valuation / 1e6).toFixed(1)}–${(m.scenarios[m.scenarios.length - 1].valuation / 1e6).toFixed(1)}M`
                        : '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Cap Table" icon={Users} tabId="cap-table">
          <div className="space-y-2">
            {(CAP_TABLE.preSeed || []).map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-gray-700 truncate">{r.shareholder}</span>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <div className="w-16 bg-gray-100 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full" style={{ width: `${r.ownership}%`, backgroundColor: BRAND_GREEN }} />
                  </div>
                  <span className="font-bold w-10 text-right" style={{ color: BRAND_GREEN }}>{r.ownership}%</span>
                </div>
              </div>
            ))}
            <div className="border-t pt-2 mt-1">
              <p className="text-[10px] text-gray-400">{CAP_TABLE.notes?.[0]}</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Due Diligence" icon={Shield} tabId="due-diligence" badge={`${ddPct}%`}>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 bg-gray-100 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: `${ddPct}%`,
                    backgroundColor: ddPct >= 80 ? BRAND_GREEN : ddPct >= 50 ? BRAND_GOLD : '#ef4444',
                  }}
                />
              </div>
              <span className="text-xs font-bold" style={{ color: BRAND_GREEN }}>{doneDD}/{totalDD}</span>
            </div>
            <div className="space-y-1.5">
              {DUE_DILIGENCE_SECTIONS.map(section => {
                const done  = section.items.filter(i => i.status === 'done').length;
                const total = section.items.length;
                const pct   = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <div key={section.id} className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 truncate">{section.title}</span>
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      <div className="w-12 bg-gray-100 rounded-full h-1">
                        <div className="h-1 rounded-full" style={{
                          width: `${pct}%`,
                          backgroundColor: pct === 100 ? BRAND_GREEN : BRAND_GOLD,
                        }} />
                      </div>
                      <span className="text-[10px] text-gray-400 w-10 text-right">{done}/{total}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionCard>
      </div>

      {/* ── Tese de Investimento ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-bold text-sm text-gray-900">Tese de Investimento</h4>
          <button
            onClick={() => onNavigate?.('thesis')}
            className="text-xs font-semibold flex items-center gap-0.5 hover:underline"
            style={{ color: BRAND_GREEN }}
          >
            Ver completo <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(INVESTOR_THESIS.whyNow || []).slice(0, 4).map((w, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: BRAND_GREEN }} />
              <span>{w}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CapTableTab = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-6">
    <h3 className="text-lg font-bold text-gray-900 mb-4">Capitalization Table — Pre-Investment</h3>
    <table className="w-full text-sm">
      <thead><tr className="border-b"><th className="text-left py-2">Shareholder</th><th className="text-left py-2">Class</th><th className="text-right py-2">Shares</th><th className="text-right py-2">%</th></tr></thead>
      <tbody>
        <tr className="border-b"><td className="py-2">Paulo Brant (Founder & CEO)</td><td>Common A</td><td className="text-right">10,000,000</td><td className="text-right font-bold">100%</td></tr>
        <tr><td className="py-2 text-gray-500">ESOP Pool (Reservado)</td><td className="text-gray-500">—</td><td className="text-right text-gray-500">—</td><td className="text-right text-gray-500">10% planejado pós-Seed</td></tr>
      </tbody>
    </table>
    <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800">
      ⚠️ Conversão LTDA → S/A planejada pré-Seed. ESOP pool de 10% será criado após captação.
    </div>
  </div>
);

const ThesisTab = () => (
  <div className="space-y-4">
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-3">Por que investir na VUKZ?</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { icon: Cpu, title: 'IA como moat', desc: '14 agentes especializados: parsing, normalização, identity resolution, validação, conteúdo social' },
          { icon: Target, title: 'Dados proprietários', desc: '+2M resultados de natação processados, grafo de identidade com 15K atletas' },
          { icon: Globe, title: 'Multi-sport expansion', desc: 'Arquitetura modular permite adicionar esportes com baixo custo marginal' },
          { icon: Users, title: 'Network effects', desc: 'Cada clube que entra aumenta valor para atletas e vice-versa (LinkedIn-style)' },
        ].map((item, i) => (
          <div key={i} className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <item.icon className="w-5 h-5" style={{ color: BRAND_GREEN }} />
              <h4 className="font-bold text-gray-900">{item.title}</h4>
            </div>
            <p className="text-sm text-gray-600">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const UseOfFundsTab = () => {
  const [round, setRound] = useState('seed');
  const roundData = USE_OF_FUNDS[round];
  const fmtBRL = (n) => `R$ ${(n / 1000).toFixed(0)}K`;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900">Uso dos Recursos</h3>
        <div className="flex gap-2">
          {['seed', 'seriesA'].map(r => (
            <button key={r} onClick={() => setRound(r)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                round === r ? 'text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-green-300'
              }`}
              style={round === r ? { backgroundColor: BRAND_GREEN } : {}}>
              {r === 'seed' ? 'Seed (R$ 1.3M)' : 'Series A (R$ 8M)'}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="text-2xl font-extrabold" style={{ color: BRAND_GREEN }}>
            {fmtBRL(roundData.total)}
          </div>
          <div className="text-sm text-gray-500">total captado neste round</div>
        </div>
        <div className="space-y-4">
          {roundData.allocation.map((item, i) => (
            <div key={i}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-gray-900">{item.category}</span>
                <span className="font-bold" style={{ color: BRAND_GREEN }}>{fmtBRL(item.amount)} ({item.pct}%)</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${item.pct}%`, backgroundColor: BRAND_GREEN }} />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-5">Fonte: projeção de uso de recursos validada pelo fundador. Valores sujeitos a ajuste conforme negociação final.</p>
      </div>
    </div>
  );
};

// ─── VALUATION TAB ──────────────────────────────────────────────────────────
const ValuationTab = () => {
  const fmtM = (n) => `R$ ${(n / 1000000).toFixed(1)}M`;
  const allScenarios = getAllValuationScenarios().filter(Boolean);
  return (
    <div className="space-y-4">
      <DataSourceBadge source="local" />
      {/* Snapshot atual */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-bold text-gray-900 mb-3">Valuation Atual (Pre-Money)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-extrabold" style={{ color: BRAND_GREEN }}>{fmtM(CURRENT_VALUATION.preMoneyBRL)}</div>
            <div className="text-xs text-gray-500 mt-1">Pre-Money (BRL)</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-extrabold" style={{ color: BRAND_GOLD }}>USD {(CURRENT_VALUATION.preMoneyUSD / 1000000).toFixed(2)}M</div>
            <div className="text-xs text-gray-500 mt-1">Pre-Money (USD)</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-sm font-semibold text-gray-700">{CURRENT_VALUATION.stage}</div>
            <div className="text-xs text-gray-500 mt-1">Estágio</div>
          </div>
        </div>
        <div className="mt-4 space-y-1.5">
          {CURRENT_VALUATION.justification.map((j, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} />
              <span>{j}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Métodos de valuation */}
      <div className="space-y-3">
        {VALUATION_METHODS.map(m => (
          <div key={m.id} className="bg-white rounded-xl border border-gray-200 p-5">
            <h4 className="font-bold text-gray-900">{m.name}</h4>
            <p className="text-xs text-gray-500 mb-3">{m.description}</p>
            {m.value && (
              <div className="text-xl font-bold mb-3" style={{ color: BRAND_GREEN }}>{fmtM(m.value)}
                {m.multiplier && <span className="text-sm font-normal text-gray-500 ml-2">(custo base {fmtM(m.rawCost)} × {m.multiplier}x)</span>}
              </div>
            )}
            {m.details && (
              <table className="w-full text-xs mb-2">
                <thead><tr className="border-b"><th className="text-left py-1 text-gray-500">Componente</th><th className="text-right text-gray-500">Dev-Meses</th><th className="text-right text-gray-500">Custo Est.</th></tr></thead>
                <tbody>
                  {m.details.map((d, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                      <td className="py-1 pl-1">{d.component}</td>
                      <td className="text-right">{d.devMonths}m</td>
                      <td className="text-right font-medium" style={{ color: BRAND_GREEN }}>{fmtM(d.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {m.scenarios && (
              <div className="grid grid-cols-3 gap-3">
                {m.scenarios.map((s, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-base font-bold" style={{ color: BRAND_GREEN }}>{fmtM(s.valuation)}</div>
                    <div className="text-xs text-gray-500">{s.label}</div>
                    <div className="text-xs text-gray-400">ARR {fmtM(s.arr)} × {s.multiple}x</div>
                  </div>
                ))}
              </div>
            )}
            {m.comparables && (
              <table className="w-full text-xs">
                <thead><tr className="border-b"><th className="text-left py-1 text-gray-500">Empresa</th><th className="text-right text-gray-500">Valuation</th><th className="text-left text-gray-500 pl-3">Estágio</th></tr></thead>
                <tbody>
                  {m.comparables.map((c, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                      <td className="py-1 pl-1 font-medium">{c.company}</td>
                      <td className="text-right">{c.valuation >= 1e9 ? `USD ${(c.valuation/1e9).toFixed(1)}B` : `USD ${(c.valuation/1e6).toFixed(0)}M`}</td>
                      <td className="pl-3 text-gray-500">{c.stage} — {c.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
      {/* Cap Table */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-bold text-gray-900 mb-3">Cap Table — Projeção de Diluição</h3>
        {['preSeed', 'postSeed', 'postSeriesA'].map(stage => (
          <div key={stage} className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{stage === 'preSeed' ? 'Pré-Seed (atual)' : stage === 'postSeed' ? 'Pós-Seed' : 'Pós-Series A'}</p>
            <table className="w-full text-xs">
              <thead><tr className="border-b"><th className="text-left py-1 text-gray-500">Acionista</th><th className="text-left text-gray-500">Classe</th><th className="text-right text-gray-500">Ações</th><th className="text-right text-gray-500">%</th></tr></thead>
              <tbody>
                {CAP_TABLE[stage].map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="py-1 pl-1 font-medium">{r.shareholder}</td>
                    <td className="text-gray-500">{r.class}</td>
                    <td className="text-right">{r.shares.toLocaleString('pt-BR')}</td>
                    <td className="text-right font-bold" style={{ color: BRAND_GREEN }}>{r.ownership}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        <div className="mt-2 space-y-1">
          {CAP_TABLE.notes.map((n, i) => (
            <p key={i} className="text-xs text-gray-500">• {n}</p>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── MARKET TAB ─────────────────────────────────────────────────────────────
const MarketTab = () => (
  <div className="space-y-4">
    <DataSourceBadge source="local" />
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="font-bold text-gray-900 mb-1">Contexto Global</h3>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold" style={{ color: BRAND_GREEN }}>{MARKET_NARRATIVE.globalContext.marketSize}</div>
          <div className="text-xs text-gray-500">Sports Analytics Global</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold" style={{ color: BRAND_GOLD }}>{MARKET_NARRATIVE.globalContext.cagr} CAGR</div>
          <div className="text-xs text-gray-500">Crescimento anual</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-sm font-bold text-gray-700">{MARKET_NARRATIVE.brazilContext.digitalPenetration}</div>
          <div className="text-xs text-gray-500">penetração analytics no BR</div>
        </div>
      </div>
      <h4 className="font-semibold text-sm text-gray-700 mb-2">Brasil</h4>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {Object.entries(MARKET_NARRATIVE.brazilContext).filter(([k]) => k !== 'digitalPenetration' && k !== 'opportunity').map(([k, v]) => (
          <div key={k} className="bg-gray-50 rounded p-2 text-xs">
            <span className="font-medium text-gray-700 capitalize">{k.replace(/([A-Z])/g, ' $1')}: </span>
            <span className="text-gray-600">{v}</span>
          </div>
        ))}
      </div>
      {MARKET_NARRATIVE.brazilContext.opportunity && (
        <p className="text-xs text-gray-500 mt-3 italic">{MARKET_NARRATIVE.brazilContext.opportunity}</p>
      )}
    </div>
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="font-bold text-gray-900 mb-3">Segmentos-Alvo</h3>
      <div className="space-y-3">
        {MARKET_NARRATIVE.segments.map((s, i) => (
          <div key={i} className="border border-gray-100 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-sm text-gray-900">{s.name}</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#d1fae5', color: BRAND_GREEN }}>{s.size}</span>
            </div>
            <p className="text-xs text-gray-500 mb-1"><strong>Dor:</strong> {s.pain}</p>
            <p className="text-xs text-gray-500 mb-1"><strong>Disposição a pagar:</strong> {s.willingness}</p>
            <p className="text-xs font-medium" style={{ color: BRAND_GREEN }}>Entrada: {s.entry}</p>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="font-bold text-gray-900 mb-3">Por que agora?</h3>
      <div className="space-y-2">
        {INVESTOR_THESIS.whyNow.map((w, i) => (
          <div key={i} className="flex items-start gap-2 text-sm">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: BRAND_GREEN }} />
            <span className="text-gray-700">{w}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── DUE DILIGENCE TAB ──────────────────────────────────────────────────────
const DueDiligenceTab = () => {
  const statusIcon = (s) => s === 'done' ? '✅' : s === 'partial' ? '🔶' : '⬜';
  const statusColor = (s) => s === 'done' ? '#064e3b' : s === 'partial' ? '#b48c2c' : '#9ca3af';
  const totalItems = DUE_DILIGENCE_SECTIONS.reduce((a, s) => a + s.items.length, 0);
  const doneItems = DUE_DILIGENCE_SECTIONS.reduce((a, s) => a + s.items.filter(i => i.status === 'done').length, 0);
  const pct = Math.round((doneItems / totalItems) * 100);
  return (
    <div className="space-y-4">
      <DataSourceBadge source="local" />
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900">Due Diligence Readiness</h3>
          <span className="text-xl font-extrabold" style={{ color: BRAND_GREEN }}>{pct}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: BRAND_GREEN }} />
        </div>
        <p className="text-xs text-gray-500">{doneItems} de {totalItems} itens completos</p>
      </div>
      {DUE_DILIGENCE_SECTIONS.map(section => (
        <div key={section.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" style={{ backgroundColor: '#f0fdf4' }}>
            <h4 className="font-bold text-sm" style={{ color: BRAND_GREEN }}>{section.title}</h4>
            <span className="text-xs text-gray-500">
              {section.items.filter(i => i.status === 'done').length}/{section.items.length} ✅
            </span>
          </div>
          <div className="divide-y divide-gray-50">
            {section.items.map(item => (
              <div key={item.id} className="px-4 py-2.5 flex items-start gap-3">
                <span className="text-sm">{statusIcon(item.status)}</span>
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-800">{item.label}</span>
                  {item.note && <p className="text-xs text-gray-500 mt-0.5">{item.note}</p>}
                </div>
                <span className="text-xs font-semibold" style={{ color: statusColor(item.status) }}>
                  {item.status === 'done' ? 'Concluído' : item.status === 'partial' ? 'Parcial' : 'Pendente'}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── FINANCIAL SUMMARY TAB ──────────────────────────────────────────────────
const FinancialSummaryTab = () => (
  <div className="space-y-4">
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="font-bold text-gray-900 mb-2">Módulo Financeiro</h3>
      <p className="text-sm text-gray-500 mb-4">
        Todos os dados financeiros detalhados — DRE, Fluxo de Caixa, Despesas, Receitas, Premissas e Projeções de Produto — estão disponíveis no módulo Financeiro.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { label: 'DRE Projetada', route: R.fin, desc: 'Receita, EBITDA, Lucro/Prejuízo por cenário' },
          { label: 'Fluxo de Caixa', route: R.fin, desc: 'Entradas, saídas, burn rate, runway' },
          { label: '9 Cenários', route: R.fin, desc: 'Matriz 3×3: investimento × operação' },
          { label: 'Despesas', route: R.finCustos, desc: 'Categorias: equipe, infra, comercial, mktg' },
          { label: 'Receitas', route: R.finReceita, desc: 'Recorrente, pontual, por produto' },
          { label: 'SwimVision', route: R.investidores, desc: 'DRE e Fluxo por produto — aba Swim Vision' },
        ].map((item, i) => (
          <a key={i} href={item.route}
            className="block border border-gray-200 rounded-lg p-3 hover:border-green-300 hover:shadow-sm transition-all">
            <div className="font-semibold text-sm" style={{ color: BRAND_GREEN }}>{item.label}</div>
            <div className="text-xs text-gray-500 mt-0.5">{item.desc}</div>
            <div className="flex items-center gap-1 mt-2 text-xs" style={{ color: BRAND_GREEN }}>
              <ChevronRight className="w-3 h-3" /> Acessar
            </div>
          </a>
        ))}
      </div>
    </div>
  </div>
);

const PlaceholderTab = ({ title }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
    <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
    <h3 className="text-lg font-bold text-gray-900">{title}</h3>
    <p className="text-sm text-gray-500 mt-1">Conteúdo em elaboração — será adicionado antes do Data Room.</p>
  </div>
);

// ─── helpers compartilhados ──────────────────────────────────────────────────
const fmtBRL = (n) => {
  if (n == null) return '—';
  return (n < 0 ? '-' : '') + 'R$ ' + Math.abs(n).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

// Monta seletor de 9 cenários local (fonte: financialScenariosData.js)
const SCENARIO_OPTIONS = (FINANCIAL_SCENARIOS || []).map(s => ({
  key: s.id,
  label: s.label,
  color: s.color,
  investment: s.investment,
}));

const ScenarioSelector = ({ selected, onSelect }) => (
  <div className="flex flex-wrap gap-2">
    {SCENARIO_OPTIONS.map(opt => (
      <button
        key={opt.key}
        onClick={() => onSelect(opt.key)}
        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
          selected === opt.key
            ? 'text-white border-transparent shadow'
            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
        }`}
        style={selected === opt.key ? { backgroundColor: opt.color || BRAND_GREEN, borderColor: opt.color || BRAND_GREEN } : {}}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

// Deriva linhas da DRE a partir dos dados anuais do cenário
const buildDRERows = (scenario) => {
  const tax = FINANCIAL_ASSUMPTIONS?.taxOnRevenue ?? 0.0933;
  return (scenario.yearly || []).map(y => {
    const rec_bruta = y.arr;
    const impostos = Math.round(rec_bruta * tax);
    const rec_liquida = rec_bruta - impostos;
    const custo_total_anual = y.costs * 12;
    const cogs = Math.round(custo_total_anual * 0.35);       // 35%: Azure, hosting, suporte direto
    const lucro_bruto = rec_liquida - cogs;
    const opex = Math.round(custo_total_anual * 0.65);       // 65%: equipe, mktg, admin
    const ebitda = lucro_bruto - opex;
    return { year: y.year, rec_bruta, impostos, rec_liquida, cogs, lucro_bruto, opex, ebitda, clientes: y.clients };
  });
};

// Deriva linhas do Fluxo de Caixa a partir dos dados anuais do cenário
const buildCashflowRows = (scenario) => {
  const inv = INVESTMENT_SCENARIOS?.[scenario.investment] || {};
  let saldo = inv.amount || 0;
  return (scenario.yearly || []).map((y, i) => {
    const recebimentos = Math.round(y.arr * 0.97);            // 97%: descontado inadimplência + prazo
    const custo_total = y.costs * 12;
    const folha      = Math.round(custo_total * 0.50);
    const azure      = Math.round(custo_total * 0.20);
    const mktg       = Math.round(custo_total * 0.15);
    const admin      = Math.round(custo_total * 0.15);
    const total_saidas = folha + azure + mktg + admin;
    const variacao = recebimentos - total_saidas;
    saldo = saldo + variacao;
    const burn_mensal = total_saidas / 12;
    const runway = burn_mensal > 0 ? Math.round(saldo / burn_mensal) : null;
    return { year: y.year, saldo_ini: saldo - variacao, recebimentos, folha, azure, mktg, admin, total_saidas, variacao, saldo_fim: saldo, burn_mensal: Math.round(burn_mensal), runway };
  });
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: DRE PROJETADA — Competência econômica com seletor de 9 cenários
// ═══════════════════════════════════════════════════════════════════════════════
const DRETab = () => {
  const [selectedKey, setSelectedKey] = useState(BASELINE_SCENARIO_KEY);
  const [dreData, setDreData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setFetchError(null);
    fetchDREForScenario(selectedKey, 12)
      .then(res => setDreData(res?.data || null))
      .catch(e => setFetchError(e?.message || 'Erro ao carregar DRE'))
      .finally(() => setLoading(false));
  }, [selectedKey]);

  const months = dreData?.months || [];
  const accounts = dreData?.accounts || [];
  const currentMonth = new Date().toISOString().slice(0, 7);
  const realizadoMonths = months.filter(m => m <= currentMonth);
  const projecaoMonths = months.filter(m => m > currentMonth);

  const fmtMonth = m => {
    const [y, mo] = m.split('-');
    const nomes = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
    return `${nomes[parseInt(mo, 10) - 1]}/${y.slice(2)}`;
  };

  const getValue = (account, month) => {
    const v = account.values?.[month];
    if (!v) return 0;
    return month <= currentMonth ? (v.real ?? 0) : (v.projected ?? 0);
  };

  return (
    <div className="space-y-4">
      {/* Seletor de cenário */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900 text-sm">Selecione o Cenário</h3>
          <DataSourceBadge source={dreData ? 'api' : (loading ? 'api' : 'local')} />
        </div>
        <ScenarioSelector selected={selectedKey} onSelect={setSelectedKey} />
      </div>

      {/* Tabela DRE */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 flex items-center justify-center gap-2">
          <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: BRAND_GREEN, borderTopColor: 'transparent' }} />
          <span className="text-sm text-gray-500">Carregando dados reais...</span>
        </div>
      ) : fetchError ? (
        <div className="bg-white rounded-xl border border-red-200 p-6 text-center">
          <p className="text-sm text-red-600">Erro: {fetchError}</p>
        </div>
      ) : accounts.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b" style={{ backgroundColor: '#f0fdf4' }}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm" style={{ color: BRAND_GREEN }}>
                DRE — Realizado + Projeção
              </h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">
                ✓ Dados reais de receitas e despesas
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Realizado: lançamentos confirmados (<code>financial_revenues</code> + <code>financial_expenses</code>).
              Projeção: entradas marcadas como previstas.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-1.5 text-left text-gray-400 min-w-[220px] sticky left-0 bg-white text-[10px]"></th>
                  {realizadoMonths.length > 0 && (
                    <th colSpan={realizadoMonths.length}
                      className="px-3 py-1 text-center text-blue-700 bg-blue-50 border-l text-[10px] font-bold uppercase tracking-wide">
                      ✓ Realizado
                    </th>
                  )}
                  {projecaoMonths.length > 0 && (
                    <th colSpan={projecaoMonths.length}
                      className="px-3 py-1 text-center text-amber-700 bg-amber-50 border-l text-[10px] font-bold uppercase tracking-wide">
                      ~ Projeção
                    </th>
                  )}
                </tr>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-2 text-left text-gray-500 sticky left-0 bg-gray-50">Linha</th>
                  {realizadoMonths.map(m => (
                    <th key={m} className="px-3 py-1.5 text-right text-gray-500 bg-blue-50/70 border-l">{fmtMonth(m)}</th>
                  ))}
                  {projecaoMonths.map((m, i) => (
                    <th key={m} className={`px-3 py-1.5 text-right text-gray-500 bg-amber-50/70 ${i === 0 ? 'border-l-2 border-amber-300' : ''}`}>
                      {fmtMonth(m)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accounts.map((acc, i) => (
                  <tr key={acc.code}
                    className={`border-b ${acc.is_subtotal ? 'bg-gray-50' : (i % 2 === 0 ? 'bg-white' : '')}`}>
                    <td
                      className={`px-4 py-1.5 text-gray-800 sticky left-0 ${acc.is_subtotal ? 'font-bold bg-gray-50' : 'font-medium bg-white'}`}
                      style={{ paddingLeft: `${16 + (acc.level || 0) * 14}px` }}>
                      {acc.name}
                    </td>
                    {realizadoMonths.map(m => {
                      const v = getValue(acc, m);
                      return (
                        <td key={m}
                          className={`px-3 py-1.5 text-right bg-blue-50/40 border-l ${acc.is_subtotal ? 'font-bold' : ''}`}
                          style={{ color: v < 0 ? '#dc2626' : BRAND_GREEN }}>
                          {v !== 0 ? fmtBRL(v) : <span className="text-gray-300">—</span>}
                        </td>
                      );
                    })}
                    {projecaoMonths.map((m, pi) => {
                      const v = getValue(acc, m);
                      return (
                        <td key={m}
                          className={`px-3 py-1.5 text-right bg-amber-50/30 ${acc.is_subtotal ? 'font-bold' : ''} ${pi === 0 ? 'border-l-2 border-amber-200' : ''}`}
                          style={{ color: v < 0 ? '#dc2626' : '#92400e' }}>
                          {v !== 0 ? fmtBRL(v) : <span className="text-gray-300">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t bg-green-50">
            <p className="text-xs text-green-700">
              ✓ Fonte: <code>financial_revenues</code> + <code>financial_expenses</code>.
              Tributos estimados Simples Nacional ~9,65%.
              Projeções = entradas marcadas como <code>is_projected=1</code>.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-sm text-gray-500">Nenhum lançamento financeiro encontrado.</p>
          <p className="text-xs text-gray-400 mt-1">Adicione receitas em <strong>Financeiro → Receitas</strong> e despesas em <strong>Financeiro → Despesas</strong>.</p>
        </div>
      )}
    </div>
  );
};

const CashflowTab = () => {
  const [selectedKey, setSelectedKey] = useState(BASELINE_SCENARIO_KEY);
  const [cfData, setCfData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setFetchError(null);
    fetchCashflowForScenario(selectedKey, 12)
      .then(res => setCfData(res?.data || null))
      .catch(e => setFetchError(e?.message || 'Erro ao carregar fluxo de caixa'))
      .finally(() => setLoading(false));
  }, [selectedKey]);

  const months = cfData?.months || [];
  const accounts = cfData?.accounts || [];
  const currentMonth = new Date().toISOString().slice(0, 7);
  const realizadoMonths = months.filter(m => m <= currentMonth);
  const projecaoMonths = months.filter(m => m > currentMonth);

  const fmtMonth = m => {
    const [y, mo] = m.split('-');
    const nomes = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
    return `${nomes[parseInt(mo, 10) - 1]}/${y.slice(2)}`;
  };

  const getValue = (account, month) => {
    const v = account.values?.[month];
    if (!v) return null;
    return month <= currentMonth ? (v.real ?? null) : (v.projected ?? null);
  };

  return (
    <div className="space-y-4">
      {/* Seletor de cenário */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900 text-sm">Selecione o Cenário</h3>
          <DataSourceBadge source={cfData ? 'api' : (loading ? 'api' : 'local')} />
        </div>
        <ScenarioSelector selected={selectedKey} onSelect={setSelectedKey} />
      </div>

      {/* Tabela Fluxo de Caixa */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 flex items-center justify-center gap-2">
          <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#1d4ed8', borderTopColor: 'transparent' }} />
          <span className="text-sm text-gray-500">Carregando dados reais...</span>
        </div>
      ) : fetchError ? (
        <div className="bg-white rounded-xl border border-red-200 p-6 text-center">
          <p className="text-sm text-red-600">Erro: {fetchError}</p>
        </div>
      ) : accounts.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b" style={{ backgroundColor: '#eff6ff' }}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-blue-900">
                Fluxo de Caixa — Realizado + Projeção
              </h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">
                ✓ Movimentos reais de caixa
              </span>
            </div>
            <p className="text-xs text-blue-700 mt-0.5">
              Regime de caixa: entradas por <code>financial_revenues.month</code>,
              saídas por <code>financial_expenses.due_date</code>. Saldo com carry-forward real.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-1.5 text-left text-gray-400 min-w-[240px] sticky left-0 bg-white text-[10px]"></th>
                  {realizadoMonths.length > 0 && (
                    <th colSpan={realizadoMonths.length}
                      className="px-3 py-1 text-center text-blue-700 bg-blue-50 border-l text-[10px] font-bold uppercase tracking-wide">
                      ✓ Realizado
                    </th>
                  )}
                  {projecaoMonths.length > 0 && (
                    <th colSpan={projecaoMonths.length}
                      className="px-3 py-1 text-center text-amber-700 bg-amber-50 border-l text-[10px] font-bold uppercase tracking-wide">
                      ~ Projeção
                    </th>
                  )}
                </tr>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-2 text-left text-gray-500 sticky left-0 bg-gray-50">Item</th>
                  {realizadoMonths.map(m => (
                    <th key={m} className="px-3 py-1.5 text-right text-gray-500 bg-blue-50/70 border-l">{fmtMonth(m)}</th>
                  ))}
                  {projecaoMonths.map((m, i) => (
                    <th key={m} className={`px-3 py-1.5 text-right text-gray-500 bg-amber-50/70 ${i === 0 ? 'border-l-2 border-amber-300' : ''}`}>
                      {fmtMonth(m)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accounts.map((acc, i) => (
                  <tr key={acc.code}
                    className={`border-b ${acc.is_subtotal ? 'bg-gray-50' : (i % 2 === 0 ? 'bg-white' : '')}`}>
                    <td
                      className={`px-4 py-1.5 text-gray-800 sticky left-0 ${acc.is_subtotal ? 'font-bold bg-gray-50' : 'font-medium bg-white'}`}
                      style={{ paddingLeft: `${16 + (acc.level || 0) * 14}px` }}>
                      {acc.name}
                    </td>
                    {realizadoMonths.map(m => {
                      const v = getValue(acc, m);
                      return (
                        <td key={m}
                          className={`px-3 py-1.5 text-right bg-blue-50/40 border-l ${acc.is_subtotal ? 'font-bold' : ''}`}
                          style={{ color: v != null && v < 0 ? '#dc2626' : BRAND_GREEN }}>
                          {v != null ? fmtBRL(v) : <span className="text-gray-300">—</span>}
                        </td>
                      );
                    })}
                    {projecaoMonths.map((m, pi) => {
                      const v = getValue(acc, m);
                      return (
                        <td key={m}
                          className={`px-3 py-1.5 text-right bg-amber-50/30 ${acc.is_subtotal ? 'font-bold' : ''} ${pi === 0 ? 'border-l-2 border-amber-200' : ''}`}
                          style={{ color: v != null && v < 0 ? '#dc2626' : '#92400e' }}>
                          {v != null ? fmtBRL(v) : <span className="text-gray-300">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t bg-blue-50">
            <p className="text-xs text-blue-700">
              ✓ Entradas: <code>financial_revenues</code>. Saídas: <code>financial_expenses</code> por <code>due_date</code>.
              Projeções = lançamentos com <code>is_projected=1</code> ou <code>scenario_type</code> diferente de <em>real</em>.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-sm text-gray-500">Nenhum lançamento de caixa encontrado.</p>
          <p className="text-xs text-gray-400 mt-1">Adicione receitas e despesas com datas de pagamento/vencimento.</p>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// PRODUTOS — Catálogo estático de cada produto
// ═══════════════════════════════════════════════════════════════════════════════
// Mix de receita por produto, por índice de ano (0 = primeiro ano do cenário)
// Soma 100% em cada linha — cada produto conversa com o ARR total do cenário
// Narrativa: SwimVision + TrackVision lançam JUNTOS (maio 2026).
// AI Agents já estão em produção no ArenaStats — monetizados standalone a partir de 2027.
// TrackVision escala mais lento (atletismo menor base que natação no Brasil).
const PRODUCT_MIX_BY_YEAR = [
  { idx: 0, swimvision: 0.75, trackvision: 0.14, aiagents: 0.11 }, // 2026: lançamento simultâneo — swim lidera
  { idx: 1, swimvision: 0.62, trackvision: 0.18, aiagents: 0.20 }, // 2027: track cresce, AI agents vira linha
  { idx: 2, swimvision: 0.52, trackvision: 0.20, aiagents: 0.28 }, // 2028: portfólio equilibra
  { idx: 3, swimvision: 0.44, trackvision: 0.22, aiagents: 0.34 }, // 2029: AI agents escalam (marketplace)
  { idx: 4, swimvision: 0.38, trackvision: 0.22, aiagents: 0.40 }, // 2030: AI agents viram maior linha
];
// Função helper: retorna share do produto para o índice de ano dado
const getProductShare = (productId, yearIdx) => {
  const mix = PRODUCT_MIX_BY_YEAR[Math.min(yearIdx, PRODUCT_MIX_BY_YEAR.length - 1)];
  return mix?.[productId] ?? 0;
};

const PRODUCTS_CATALOG = {
  swimvision: {
    id: 'swimvision',
    name: 'SwimVision',
    tagline: 'Visão computacional para natação — análise de vídeo vinculada ao resultado',
    icon: '🏊',
    color: '#0ea5e9',
    royaltyRate: 0.15,    // 15% para parceiro Vukz (câmeras + integração)
    azureShare: 0.18,     // custo Azure/Infra como % da receita bruta do produto
    fixoMensal: 5000,
    pricingNote: 'Todos os planos incluem acesso a resultados, competições e vínculo vídeo ↔ resultado. Diferencial: volume de atletas e créditos de análise IA/mês.',
    plans: [
      {
        name: 'Individual',
        price: 149,
        periodo: '/mês',
        highlight: false,
        athletes: 5,
        tokens: 20,
        storage: '5 GB',
        features: [
          'Até 5 atletas gerenciados',
          '20 análises de vídeo IA/mês',
          'Resultados e competições — todos',
          'Vínculo vídeo ↔ resultado da prova',
          'Feed social do atleta',
          '5 GB de storage de vídeo',
          'Suporte por email',
        ],
        example: 'Ideal para: atleta independente ou família que quer acompanhar 1–5 nadadores.',
      },
      {
        name: 'Grupo',
        price: 349,
        periodo: '/mês',
        highlight: false,
        athletes: 10,
        tokens: 60,
        storage: '20 GB',
        features: [
          'Até 10 atletas gerenciados',
          '60 análises de vídeo IA/mês',
          'Resultados e competições — todos',
          'Vínculo vídeo ↔ resultado da prova',
          'Dashboard comparativo entre atletas',
          'Ranking interno do grupo',
          '20 GB de storage de vídeo',
          'Relatórios PDF mensais',
          'Suporte email + WhatsApp',
        ],
        example: 'Ideal para: treinador autônomo, escolinha ou turma de treinamento.',
      },
      {
        name: 'Clube',
        price: 790,
        periodo: '/mês',
        highlight: true,
        athletes: 20,
        tokens: 150,
        storage: '60 GB',
        features: [
          'Até 20 atletas gerenciados',
          '150 análises de vídeo IA/mês',
          'Resultados e competições — todos',
          'Vínculo vídeo ↔ resultado da prova',
          'Dashboard clube completo + relatórios',
          'Comparativo com federação / estadual',
          'Categorias e convocações automáticas',
          '60 GB de storage de vídeo',
          'Exportação de dados (CSV / PDF)',
          'Suporte prioritário',
        ],
        example: 'Ideal para: clube de médio porte com equipe de competição ativa.',
      },
      {
        name: 'Personalizado',
        price: null,
        periodo: '',
        highlight: false,
        athletes: null,
        tokens: null,
        storage: 'Ilimitado',
        features: [
          'Número de atletas sob medida',
          'Volume de tokens IA negociado',
          'Storage ilimitado',
          'Resultados e competições — todos',
          'Vínculo vídeo ↔ resultado da prova',
          'Integração CBDA / federações estaduais',
          'White-label e API aberta',
          'Todos os 14 Agentes Vukz incluídos',
          'SLA 99,9% + Onboarding dedicado',
          'Clube ancora / federação / scaling',
        ],
        example: 'Para clubes grandes, federações estaduais ou nacionais. Preço sob consulta.',
      },
    ],
    description: 'Análise de vídeo com IA para natação competitiva. O diferencial central é vincular o vídeo da prova diretamente ao resultado oficial, permitindo que treinadores e atletas revisitem cada detalhe com contexto de performance. Sem limitação por piscina — o modelo escala por volume de atletas e créditos de análise IA.',
    differentials: [
      'Vídeo vinculado ao resultado oficial da prova',
      'Sem limite por piscina — escala por atletas e tokens',
      'Todos os planos acessam resultados e competições completos',
      'Parceria Vukz (câmeras + integração, sem hardware próprio)',
      'Compatível com CBDA e federações estaduais',
      'Azure AI Vision + OpenAI para análise de técnica',
    ],
    status: 'MVP lançado — pilotos ativos',
    trl: 'TRL 6',
  },
  trackvision: {
    id: 'trackvision',
    name: 'TrackVision',
    tagline: 'Visão computacional para atletismo, corrida e esportes de campo',
    icon: '🏃',
    color: '#f97316',
    royaltyRate: 0.15,     // 15% ao parceiro após descontar infra
    azureShare: 0.16,     // custo Azure/Infra como % da receita bruta
    fixoMensal: 5000,
    // ── Modelo de precificação: base clube + por grupo de atletas ──
    // Referência Pinheiros (SP): ~80 atletas no atletismo = 8 grupos de 10
    // Exemplo Pro: R$ 2.490 + 8 × R$ 390 = R$ 5.610/mês
    pricingNote: 'Base clube + R$ por grupo de 10 atletas. Ex: Pinheiros (80 atletas) no Pro = R$ 2.490 + 8×R$390 = R$ 5.610/mês.',
    plans: [
      {
        name: 'Club Start',
        price: 990,
        periodo: '/mês',
        pricePerGroup: 220,
        groupSize: 10,
        highlight: false,
        features: [
          'Base: R$ 990/mês (até 20 atletas incluídos)',
          '+ R$ 220/grupo adicional de 10 atletas',
          '1 modalidade (corrida, salto ou arremesso)',
          'Dashboard clube + perfis de atletas',
          'Análise de velocidade e progressão',
          'Agentes: ArenaStats + Feed Social',
          'Suporte email',
        ],
        example: 'Ex: 40 atletas = R$ 990 + 2×R$220 = R$ 1.430/mês',
      },
      {
        name: 'Club Pro',
        price: 2490,
        periodo: '/mês',
        pricePerGroup: 390,
        groupSize: 10,
        highlight: true,
        features: [
          'Base: R$ 2.490/mês (até 30 atletas incluídos)',
          '+ R$ 390/grupo adicional de 10 atletas',
          'Multi-modalidade (corrida + salto + arremesso)',
          'Análise biomecânica básica + vídeo slow-motion',
          'Comparação histórica + ranking interno',
          'Agentes: Parser + Sentinel + Analytics + Feed',
          'Suporte prioritário',
        ],
        example: 'Ex: Pinheiros (80 at.) = R$ 2.490 + 5×R$390 = R$ 4.440/mês',
      },
      {
        name: 'Club Elite',
        price: 4990,
        periodo: '/mês',
        pricePerGroup: 590,
        groupSize: 10,
        highlight: false,
        features: [
          'Base: R$ 4.990/mês (até 50 atletas incluídos)',
          '+ R$ 590/grupo adicional de 10 atletas',
          'Atletas ilimitados (sem teto)',
          'IA personalizada por atleta + plano de treino',
          'White-label + API aberta',
          'Todos os 14 Agentes Vukz',
          'Integração CBAt/Federações estaduais',
          'SLA 99,9% + onboarding dedicado',
        ],
        example: 'Ex: clube 150 at. = R$ 4.990 + 10×R$590 = R$ 10.890/mês',
      },
      {
        name: 'Federação',
        price: 14900,
        periodo: '/mês',
        pricePerGroup: 490,
        groupSize: 1,
        highlight: false,
        features: [
          'Base: R$ 14.900/mês (até 5 clubes filiados)',
          '+ R$ 490/clube adicional filiado',
          'Painel federal + rankings oficiais automáticos',
          'Homologação de resultados (CBAt/estaduais)',
          'Todos os atletas dos clubes filiados',
          'Todos os 14 Agentes Vukz para a federação',
          'Onboarding dedicado + SLA',
        ],
        example: 'Ex: federação com 20 clubes = R$ 14.900 + 15×R$490 = R$ 22.250/mês',
      },
    ],
    description:
      'Plataforma de visão computacional para atletismo, corrida e esportes de campo. ' +
      'Lançamento simultâneo com SwimVision — modelo já extrai métricas reais de velocidade, ' +
      'aceleração e técnica de corrida. Precificação base clube + por grupo de atletas, ' +
      'garantindo previsibilidade para o clube e escala de receita para a Vukz.',
    differentials: [
      'Lançamento simultâneo com SwimVision — modelo em produção',
      'Base + por grupo: clube só paga pelo que usa',
      'Referência Pinheiros (SP) como clube ancora',
      'Multi-sport: corrida, salto, arremesso, field',
      'Sem wearables — câmera suficiente',
      'Todos os 14 Agentes Vukz incluídos no Elite',
    ],
    status: 'MVP — lançamento simultâneo com SwimVision',
    trl: 'TRL 7',
  },
  aiagents: {
    id: 'aiagents',
    name: 'AI Agents Suite',
    tagline: 'Conjunto de agentes de IA para gestão esportiva',
    icon: '🤖',
    color: '#8b5cf6',
    royaltyRate: 0,       // sem royalty — produto 100% Vukz
    azureShare: 0.25,     // maior custo de API GPT
    fixoMensal: 8000,
    plans: [
      { name: 'Starter (3 agentes)', price: 499,  periodo: '/mês', highlight: false, features: ['Agente de Resultados', 'Agente de Feed Social', 'Agente de Convocações básico', 'Azure AI Foundry (gpt-4o-mini)', '1.000 chamadas/mês'] },
      { name: 'Pro (8 agentes)', price: 1490, periodo: '/mês', highlight: true, features: ['Todos do Starter', '+Parser de Resultados', '+Sentinel de Validação', '+Normalizer', '+Analytics Agent', '+Orchestrator', '10.000 chamadas/mês', 'gpt-4o'] },
      { name: 'Enterprise (14 agentes)', price: 3490, periodo: '/mês', highlight: false, features: ['Todos os 14 agentes', 'Personalização por domínio', 'White-label', 'API própria', 'Purview Governance', 'Chamadas ilimitadas', 'SLA + Suporte dedicado'] },
      { name: 'Marketplace (por agente)', price: 199, periodo: '/agente/mês', highlight: false, features: ['Compra individual de agentes', 'Integração com stack do cliente', 'Documentação OpenAPI', 'Suporte comunidade'] },
    ],
    description: '14 agentes de IA orquestrados para automação de gestão esportiva: ingestão de dados, validação, publicação social, analytics, e onboarding. Baseados em Azure AI Foundry + gpt-4o.',
    differentials: ['14 agentes especializados em esporte', 'OpenAPI para integração com qualquer stack', 'Azure AI Foundry + Microsoft Purview', 'Multi-sport: natação, futebol, atletismo'],
    status: 'Produção — ArenaStats já usa todos os 14 agentes',
    trl: 'TRL 8',
  },
};

// Deriva DRE por produto: share do ARR varia por ano (mix evolutivo)
const buildProductDRE = (scenario, product) => {
  const tax = FINANCIAL_ASSUMPTIONS?.taxOnRevenue ?? 0.0933;
  return (scenario.yearly || []).map((y, idx) => {
    const share     = getProductShare(product.id, idx);
    const rec_bruta = Math.round(y.arr * share);
    const impostos  = Math.round(rec_bruta * tax);
    const rec_liq   = rec_bruta - impostos;
    const azure     = Math.round(rec_bruta * product.azureShare);   // infra primeiro
    const base_roy  = rec_liq - azure;                              // base para royalties = líquida - infra
    const royalties = Math.round(base_roy * product.royaltyRate);   // royalties sobre o que sobrou
    const margem    = base_roy - royalties;
    const clientes  = Math.round(y.clients * share);
    return { year: y.year, share, rec_bruta, impostos, rec_liq, azure, royalties, margem, clientes, arr_total: y.arr };
  });
};

// Deriva Cashflow por produto: saldo inicial proporcional ao investimento recebido
const buildProductCashflow = (scenario, product) => {
  const inv = INVESTMENT_SCENARIOS?.[scenario.investment] || {};
  // saldo inicial = share do produto no primeiro ano × investimento recebido
  const firstShare = getProductShare(product.id, 0);
  let saldo = Math.round((inv.amount || 0) * firstShare);
  return (scenario.yearly || []).map((y, idx) => {
    const share        = getProductShare(product.id, idx);
    const recebimentos = Math.round(y.arr * share * 0.97); // 97%: lag de 30d + inadimplência
    const royalties    = Math.round(recebimentos * product.royaltyRate);
    const azure        = Math.round(recebimentos * product.azureShare);
    const fixo         = product.fixoMensal * 12;
    const total_saidas = royalties + azure + fixo;
    const variacao     = recebimentos - total_saidas;
    saldo             += variacao;
    const burn_mensal  = Math.round(total_saidas / 12);
    const runway       = burn_mensal > 0 ? Math.round(saldo / burn_mensal) : null;
    return { year: y.year, share, recebimentos, royalties, azure, fixo, total_saidas, variacao, saldo_fim: saldo, burn_mensal, runway };
  });
};

// Aba principal de Produtos
const ProductsTab = () => {
  const [selectedProduct, setSelectedProduct] = useState('swimvision');
  const [selectedKey, setSelectedKey] = useState(BASELINE_SCENARIO_KEY);
  const [subTab, setSubTab] = useState('dre');

  const product = PRODUCTS_CATALOG[selectedProduct];
  const scenario = (FINANCIAL_SCENARIOS || []).find(s => s.id === selectedKey) || FINANCIAL_SCENARIOS?.[0];

  const dreRows  = scenario ? buildProductDRE(scenario, product) : [];
  const cfRows   = scenario ? buildProductCashflow(scenario, product) : [];

  const SUB_TABS = [
    { id: 'dre',       label: 'DRE Projetada' },
    { id: 'cashflow',  label: 'Fluxo de Caixa' },
    { id: 'parceiro',  label: 'Visão Parceiro' },
    { id: 'detalhe',   label: 'Detalhamento' },
  ];

  const DRE_LINES = [
    { key: 'rec_bruta', label: '(+) Receita Bruta',                bold: false, color: BRAND_GREEN  },
    { key: 'impostos',  label: '(-) Impostos (9,33%)',             bold: false, color: '#dc2626', neg: true },
    { key: 'rec_liq',   label: '= Receita Líquida',                bold: true,  color: BRAND_GREEN  },
    { key: 'azure',     label: '(-) Infra / Azure',                bold: false, color: '#dc2626', neg: true },
    { key: 'royalties', label: '(-) Royalties Parceiro (15%)',     bold: false, color: '#dc2626', neg: true },
    { key: 'margem',    label: '= Margem Bruta Produto',           bold: true,  color: BRAND_GREEN  },
  ];

  const CF_LINES = [
    { key: 'recebimentos', label: '(+) Recebimentos',       bold: false, color: BRAND_GREEN  },
    { key: 'royalties',    label: '(-) Royalties',          bold: false, color: '#dc2626', neg: true },
    { key: 'azure',        label: '(-) Azure / Infra',      bold: false, color: '#dc2626', neg: true },
    { key: 'fixo',         label: '(-) Fixo Alocado',       bold: false, color: '#dc2626', neg: true },
    { key: 'total_saidas', label: '= Total Desembolsos',    bold: true,  color: '#dc2626', neg: true },
    { key: 'variacao',     label: '= Variação Líquida',     bold: true,  color: BRAND_GOLD  },
    { key: 'saldo_fim',    label: '= Saldo Acumulado',      bold: true,  color: BRAND_GREEN  },
    { key: 'burn_mensal',  label: 'Burn Rate Mensal',       bold: false, color: '#dc2626', neg: false },
    { key: 'runway',       label: 'Runway (meses)',         bold: false, color: BRAND_GREEN, unit: ' m' },
  ];

  return (
    <div className="space-y-4">

      {/* ── Seletores topo ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        {/* Produto */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Produto</p>
          <div className="flex flex-wrap gap-2">
            {Object.values(PRODUCTS_CATALOG).map(p => (
              <button
                key={p.id}
                onClick={() => { setSelectedProduct(p.id); setSubTab('dre'); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all border-2 ${
                  selectedProduct === p.id
                    ? 'text-white border-transparent shadow-md'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                }`}
                style={selectedProduct === p.id ? { backgroundColor: p.color, borderColor: p.color } : {}}
              >
                <span className="text-base">{p.icon}</span>
                {p.name}
                {selectedProduct === p.id && (
                  <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">{p.trl}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Cenário */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cenário Financeiro</p>
          <ScenarioSelector selected={selectedKey} onSelect={setSelectedKey} />
        </div>

        {/* Resumo do produto selecionado */}
        <div className="pt-2 border-t border-gray-100 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
          <span style={{ color: product.color }} className="font-semibold">{product.icon} {product.name}</span>
          <span>{product.tagline}</span>
          <span className="px-2 py-0.5 rounded-full text-white text-[10px]" style={{ backgroundColor: product.color }}>{product.status}</span>
          {product.royaltyRate > 0 && <span>Royalty parceiro: {(product.royaltyRate * 100).toFixed(0)}%</span>}
          <span>Share ARR: {(getProductShare(product.id, 0)*100).toFixed(0)}%→{(getProductShare(product.id, 4)*100).toFixed(0)}% (Ano1→Ano5)</span>
        </div>
      </div>

      {/* ── Sub-tabs ── */}
      <div className="flex gap-2">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              subTab === t.id ? 'text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400'
            }`}
            style={subTab === t.id ? { backgroundColor: product.color } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ════ DRE ════ */}
      {subTab === 'dre' && (
        dreRows.length > 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b" style={{ backgroundColor: '#f0fdf4' }}>
              <h3 className="font-bold text-sm" style={{ color: product.color }}>
                DRE Produto — {product.name} / {scenario?.label}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Competência econômica. Share do ARR por produto evolui: {(getProductShare(product.id,0)*100).toFixed(0)}%→{(getProductShare(product.id,2)*100).toFixed(0)}%→{(getProductShare(product.id,4)*100).toFixed(0)}% (Ano1→Ano3→Ano5).
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-4 py-2 text-left text-gray-500 min-w-[220px]">Linha</th>
                    {dreRows.map(r => <th key={r.year} className="px-3 py-2 text-right text-gray-500">{r.year}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {DRE_LINES.map((line, i) => (
                    <tr key={line.key} className={`border-b ${line.bold ? 'bg-gray-50' : i % 2 === 0 ? 'bg-white' : ''}`}>
                      <td className={`px-4 py-2 ${line.bold ? 'font-bold' : 'font-medium'} text-gray-800`}>{line.label}</td>
                      {dreRows.map(r => {
                        const v = r[line.key];
                        const display = line.neg ? -v : v;
                        return (
                          <td key={r.year} className={`px-3 py-2 text-right ${line.bold ? 'font-bold' : ''}`}
                            style={{ color: (line.key === 'margem' && v < 0) ? '#dc2626' : line.color }}>
                            {fmtBRL(display)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="border-b bg-white">
                    <td className="px-4 py-2 text-gray-400 text-[11px]">Margem Bruta %</td>
                    {dreRows.map(r => {
                      const pct = r.rec_bruta > 0 ? ((r.margem / r.rec_bruta) * 100).toFixed(1) : '—';
                      return <td key={r.year} className="px-3 py-2 text-right text-gray-500 text-[11px]">{pct}%</td>;
                    })}
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="px-4 py-2 text-gray-400 text-[11px]">% do ARR total Vukz</td>
                    {dreRows.map(r => (
                      <td key={r.year} className="px-3 py-2 text-right text-gray-500 text-[11px]">{(r.share*100).toFixed(0)}%</td>
                    ))}
                  </tr>
                  <tr className="border-b bg-white">
                    <td className="px-4 py-2 text-gray-400 text-[11px]">ARR total Vukz (referência)</td>
                    {dreRows.map(r => (
                      <td key={r.year} className="px-3 py-2 text-right text-gray-400 text-[11px]">{fmtBRL(r.arr_total)}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 border-t text-xs text-amber-700 bg-amber-50">
              Share do ARR varia por ano (mix evolutivo). Royalties: {(product.royaltyRate*100).toFixed(0)}%. Azure: {(product.azureShare*100).toFixed(0)}% da rec. bruta. Soma dos 3 produtos = 100% do ARR total Vukz.
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border p-8 text-center text-sm text-gray-500">Sem dados para este cenário.</div>
        )
      )}

      {/* ════ FLUXO DE CAIXA ════ */}
      {subTab === 'cashflow' && (
        cfRows.length > 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b" style={{ backgroundColor: '#eff6ff' }}>
              <h3 className="font-bold text-sm text-blue-900">
                Fluxo de Caixa — {product.name} / {scenario?.label}
              </h3>
              <p className="text-xs text-blue-700 mt-0.5">Regime de caixa. Share evoluindo — soma dos 3 produtos = ARR total. Recebimentos = ARR×share×97%.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-4 py-2 text-left text-gray-500 min-w-[220px]">Item</th>
                    {cfRows.map(r => <th key={r.year} className="px-3 py-2 text-right text-gray-500">{r.year}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {CF_LINES.map((line, i) => (
                    <tr key={line.key} className={`border-b ${line.bold ? 'bg-gray-50' : i % 2 === 0 ? 'bg-white' : ''}`}>
                      <td className={`px-4 py-2 ${line.bold ? 'font-bold' : 'font-medium'} text-gray-800`}>{line.label}</td>
                      {cfRows.map(r => {
                        const v = r[line.key];
                        if (v == null) return <td key={r.year} className="px-3 py-2 text-right text-gray-300">—</td>;
                        if (line.unit) return <td key={r.year} className={`px-3 py-2 text-right ${line.bold ? 'font-bold' : ''}`}
                          style={{ color: line.color }}>{v}{line.unit}</td>;
                        const display = line.neg ? -v : v;
                        return (
                          <td key={r.year} className={`px-3 py-2 text-right ${line.bold ? 'font-bold' : ''}`}
                            style={{ color: (line.key === 'saldo_fim' || line.key === 'variacao') && v < 0 ? '#dc2626' : line.color }}>
                            {fmtBRL(display)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 border-t text-xs text-blue-700 bg-blue-50">
              Fixo alocado: {fmtBRL(product.fixoMensal)}/mês. Royalties: {(product.royaltyRate*100).toFixed(0)}% sobre recebimentos. Azure: {(product.azureShare*100).toFixed(0)}% sobre rec. bruta.
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border p-8 text-center text-sm text-gray-500">Sem dados para este cenário.</div>
        )
      )}

      {/* ════ VISÃO PARCEIRO ════ */}
      {subTab === 'parceiro' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b" style={{ backgroundColor: '#fffbeb' }}>
            <h3 className="font-bold text-sm" style={{ color: BRAND_GOLD }}>Visão Parceiro — {product.name}</h3>
            <p className="text-xs text-gray-500">Sem custos internos. Seguro para compartilhar externamente com parceiros e clubes.</p>
          </div>
          {product.royaltyRate > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-4 py-2 text-left text-gray-500">Ano</th>
                    <th className="px-3 py-2 text-right text-gray-400">Share</th>
                    <th className="px-3 py-2 text-right text-gray-500">Clientes</th>
                    <th className="px-3 py-2 text-right text-gray-500">Rec. Total</th>
                    <th className="px-3 py-2 text-right text-gray-500">Impostos</th>
                    <th className="px-3 py-2 text-right" style={{ color: BRAND_GOLD }}>Parte Parceiro</th>
                    <th className="px-3 py-2 text-right" style={{ color: BRAND_GREEN }}>Parte Vukz</th>
                    <th className="px-3 py-2 text-right text-gray-500">Royalty %</th>
                  </tr>
                </thead>
                <tbody>
                  {(scenario?.yearly || []).map((y, i) => {
                    const share = getProductShare(product.id, i);
                    const rec = Math.round(y.arr * share);
                    const tax = Math.round(rec * (FINANCIAL_ASSUMPTIONS?.taxOnRevenue ?? 0.0933));
                    const recLiq = rec - tax;
                    const parceiro = Math.round(recLiq * product.royaltyRate);
                    const vukz = recLiq - parceiro;
                    return (
                      <tr key={y.year} className={i % 2 === 0 ? 'bg-white' : 'bg-amber-50'}>
                        <td className="px-4 py-2 font-medium text-gray-700">{y.year}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{(share*100).toFixed(0)}%</td>
                        <td className="px-3 py-2 text-right text-gray-600">{y.clients > 0 ? Math.round(y.clients * share) : '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{fmtBRL(rec)}</td>
                        <td className="px-3 py-2 text-right text-red-400">{fmtBRL(-tax)}</td>
                        <td className="px-3 py-2 text-right font-bold" style={{ color: BRAND_GOLD }}>{fmtBRL(parceiro)}</td>
                        <td className="px-3 py-2 text-right font-bold" style={{ color: BRAND_GREEN }}>{fmtBRL(vukz)}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{(product.royaltyRate * 100).toFixed(0)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6">
              <div className="flex items-start gap-3 bg-purple-50 rounded-xl p-4">
                <span className="text-2xl">🤖</span>
                <div>
                  <p className="font-semibold text-purple-900 text-sm">Produto 100% Vukz — sem parceiro de royalty</p>
                  <p className="text-xs text-purple-700 mt-1">
                    AI Agents Suite é desenvolvido e operado integralmente pela Vukz. Toda a receita é retida.
                    Não há split de receita com parceiro externo neste produto.
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {(scenario?.yearly || []).map((y, i) => {
                      const share = getProductShare(product.id, i);
                      const rec = Math.round(y.arr * share);
                      return (
                        <div key={y.year} className="bg-white rounded-lg p-3 border border-purple-100">
                          <div className="text-xs text-gray-400 font-semibold">{y.year} ({(share*100).toFixed(0)}% ARR)</div>
                          <div className="text-lg font-extrabold" style={{ color: product.color }}>{fmtBRL(rec)}</div>
                          <div className="text-xs text-gray-500">{Math.round(y.clients * share)} clientes</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
          <p className="text-xs text-gray-400 px-4 py-2 border-t">
            Valores sem custos operacionais internos. Dados derivados do cenário selecionado.
          </p>
        </div>
      )}

      {/* ════ DETALHAMENTO ════ */}
      {subTab === 'detalhe' && (
        <div className="space-y-4">
          {/* Descrição & Diferenciais */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0"
                style={{ backgroundColor: product.color + '18' }}>
                {product.icon}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="text-xl font-extrabold" style={{ color: product.color }}>{product.name}</h3>
                  <span className="px-2 py-1 rounded-full text-white text-xs font-bold" style={{ backgroundColor: product.color }}>
                    {product.status}
                  </span>
                  <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">{product.trl}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{product.tagline}</p>
                <p className="text-sm text-gray-700 mt-2">{product.description}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-3">
              {product.differentials.map((d, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
                  <span className="mt-0.5 flex-shrink-0" style={{ color: product.color }}>✦</span>
                  {d}
                </div>
              ))}
            </div>
          </div>

          {/* Planos de preço */}
          {product.pricingNote && (
            <div className="bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 flex items-start gap-3">
              <span className="text-xl flex-shrink-0">💡</span>
              <div>
                <p className="text-xs font-bold text-sky-800">Modelo de Precificação</p>
                <p className="text-xs text-sky-700 mt-0.5">{product.pricingNote}</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {product.plans.map(plan => (
              <div
                key={plan.name}
                className={`relative bg-white rounded-xl border-2 p-5 flex flex-col ${
                  plan.highlight ? 'shadow-lg' : 'border-gray-200'
                }`}
                style={plan.highlight ? { borderColor: product.color } : {}}
              >
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 text-white text-[10px] font-bold rounded-full shadow"
                      style={{ backgroundColor: product.color }}>MAIS POPULAR</span>
                  </div>
                )}
                <div className="mb-3">
                  <h4 className="font-bold text-gray-900 text-sm">{plan.name}</h4>
                  <div className="mt-1 flex items-baseline gap-1">
                    {plan.price != null ? (
                      <>
                        <span className="text-2xl font-extrabold" style={{ color: product.color }}>
                          R$ {plan.price.toLocaleString('pt-BR')}
                        </span>
                        <span className="text-xs text-gray-400">{plan.periodo}</span>
                      </>
                    ) : (
                      <span className="text-lg font-extrabold" style={{ color: product.color }}>Sob consulta</span>
                    )}
                  </div>
                  {plan.pricePerGroup && (
                    <div className="mt-1 text-xs font-semibold rounded-lg px-2 py-1 inline-block"
                      style={{ backgroundColor: product.color + '15', color: product.color }}>
                      + R$ {plan.pricePerGroup.toLocaleString('pt-BR')}/grupo de {plan.groupSize} atletas
                    </div>
                  )}
                  {/* Métricas principais: atletas / tokens / storage */}
                  {(plan.athletes != null || plan.tokens != null || plan.storage) && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {plan.athletes != null && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-blue-50 text-blue-700">
                          👤 {plan.athletes} atletas
                        </span>
                      )}
                      {plan.tokens != null && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-purple-50 text-purple-700">
                          ⚡ {plan.tokens} tokens IA/mês
                        </span>
                      )}
                      {plan.storage && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-gray-100 text-gray-600">
                          💾 {plan.storage}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <ul className="flex-1 space-y-1.5 mt-3 border-t pt-3">
                  {plan.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-xs text-gray-600">
                      <span className="mt-0.5 flex-shrink-0" style={{ color: product.color }}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                {plan.example && (
                  <div className="mt-3 pt-3 border-t border-dashed">
                    <p className="text-[10px] text-gray-400 italic">{plan.example}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* KPIs do produto no cenário selecionado */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h4 className="font-bold text-gray-900 text-sm mb-1">Projeção do Produto — {scenario?.label}</h4>
            <p className="text-xs text-gray-400 mb-3">ARR produto = share evoluindo × ARR total Vukz. Soma SwimVision + TrackVision + AI Agents = 100% do ARR.</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {(scenario?.yearly || []).slice(0, 5).map((y, i) => {
                const share  = getProductShare(product.id, i);
                const rec    = Math.round(y.arr * share);
                const margem = Math.round(rec * (1 - (FINANCIAL_ASSUMPTIONS?.taxOnRevenue ?? 0.0933) - product.royaltyRate - product.azureShare));
                return (
                  <div key={y.year} className="rounded-xl p-3 border text-center" style={{ borderColor: product.color + '40', backgroundColor: product.color + '08' }}>
                    <div className="text-xs text-gray-500 font-semibold">{y.year}</div>
                    <div className="text-[10px] font-bold rounded-full px-2 mt-0.5" style={{ color: product.color }}>{(share*100).toFixed(0)}% ARR</div>
                    <div className="text-base font-extrabold mt-1" style={{ color: product.color }}>{fmtBRL(rec)}</div>
                    <div className="text-[10px] text-gray-400">ARR produto</div>
                    <div className="text-sm font-bold mt-1" style={{ color: margem >= 0 ? BRAND_GREEN : '#dc2626' }}>{fmtBRL(margem)}</div>
                    <div className="text-[10px] text-gray-400">Margem bruta</div>
                    <div className="text-[10px] text-gray-500 mt-1">{Math.round(y.clients * share)} clientes</div>
                  </div>
                );
              })}
            </div>
            {/* Reconciliação: 3 produtos somam ARR total */}
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs font-semibold text-gray-500 mb-2">Reconc. ARR — verificação: soma dos 3 produtos = ARR total Vukz</p>
              <div className="overflow-x-auto">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-1.5 text-left text-gray-400">Produto</th>
                      {(scenario?.yearly || []).map(y => <th key={y.year} className="px-3 py-1.5 text-right text-gray-400">{y.year}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(PRODUCTS_CATALOG).map(p => (
                      <tr key={p.id} className="border-t">
                        <td className="px-3 py-1.5 font-medium" style={{ color: p.color }}>{p.icon} {p.name}</td>
                        {(scenario?.yearly || []).map((y, i) => (
                          <td key={y.year} className="px-3 py-1.5 text-right text-gray-600">
                            {fmtBRL(Math.round(y.arr * getProductShare(p.id, i)))}
                            <span className="ml-1 text-gray-300">({(getProductShare(p.id,i)*100).toFixed(0)}%)</span>
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="border-t bg-gray-50 font-bold">
                      <td className="px-3 py-1.5" style={{ color: BRAND_GREEN }}>= Total Vukz</td>
                      {(scenario?.yearly || []).map(y => (
                        <td key={y.year} className="px-3 py-1.5 text-right" style={{ color: BRAND_GREEN }}>{fmtBRL(y.arr)}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const SwimVisionTab = ({ scenarioKey }) => {
  const [subTab, setSubTab] = useState('dre');
  const [dreData, setDreData] = useState(null);
  const [cashflowData, setCashflowData] = useState(null);
  const [partnerData, setPartnerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState('empty');
  const fmt = fmtBRL;

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      const [dreRes, cfRes, pvRes] = await Promise.allSettled([
        fetchSwimVisionDRE(scenarioKey, 12),
        fetchSwimVisionCashflow(scenarioKey, 12),
        fetchSwimVisionPartnerView(scenarioKey, 12),
      ]);
      if (dreRes.status === 'fulfilled' && dreRes.value?.success) {
        setDreData(dreRes.value.data);
        setSource(dreRes.value.source || 'backend');
      }
      if (cfRes.status === 'fulfilled' && cfRes.value?.success) setCashflowData(cfRes.value.data);
      if (pvRes.status === 'fulfilled' && pvRes.value?.success) setPartnerData(pvRes.value.data);
      setLoading(false);
    };
    loadAll();
  }, [scenarioKey]);

  const SUB_TABS = [
    { id: 'dre', label: 'DRE Investidor' },
    { id: 'cashflow', label: 'Fluxo de Caixa' },
    { id: 'partner', label: 'Visão Parceiro' },
  ];

  return (
    <div className="space-y-4">
      <DataSourceBadge source={source} />

      {/* Sub-tab bar */}
      <div className="flex gap-2">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              subTab === t.id
                ? 'text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:border-green-300'
            }`}
            style={subTab === t.id ? { backgroundColor: BRAND_GREEN } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: BRAND_GREEN }} />
          <p className="text-sm text-gray-500 mt-2">Carregando dados SwimVision...</p>
        </div>
      ) : (
        <>
          {/* DRE Investidor */}
          {subTab === 'dre' && (
            dreData?.rows?.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100" style={{ backgroundColor: '#f0fdf4' }}>
                  <h3 className="font-bold text-sm" style={{ color: BRAND_GREEN }}>DRE SwimVision — {dreData.meta?.clubes_ativos ?? 0} clubes ativos</h3>
                  {dreData.meta?.margem_pct != null && (
                    <p className="text-xs text-gray-500">Margem bruta média: {dreData.meta.margem_pct.toFixed(1)}%</p>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2 text-left text-gray-500">Mês</th>
                        <th className="px-3 py-2 text-right text-gray-500">Rec. Bruta</th>
                        <th className="px-3 py-2 text-right text-gray-500">Impostos</th>
                        <th className="px-3 py-2 text-right text-gray-500">Rec. Líquida</th>
                        <th className="px-3 py-2 text-right text-gray-500">Royalties</th>
                        <th className="px-3 py-2 text-right text-gray-500">Custos Var.</th>
                        <th className="px-3 py-2 text-right font-semibold" style={{ color: BRAND_GREEN }}>Margem Bruta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dreData.rows.map((r, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-2 font-medium text-gray-700">{r.mes}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{fmt(r.receita_bruta)}</td>
                          <td className="px-3 py-2 text-right text-red-400">{fmt(r.impostos)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{fmt(r.receita_liquida)}</td>
                          <td className="px-3 py-2 text-right text-red-400">{fmt(r.royalties_pagos)}</td>
                          <td className="px-3 py-2 text-right text-red-400">{fmt(r.custos_variaveis)}</td>
                          <td className="px-3 py-2 text-right font-bold" style={{ color: r.margem_bruta >= 0 ? BRAND_GREEN : '#dc2626' }}>{fmt(r.margem_bruta)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-400 px-4 py-2">DRE do produto SwimVision. Inclui impostos (9,33%), royalties pagos à Vukz e custos variáveis de infra.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <p className="text-sm text-gray-500">Nenhum dado de DRE SwimVision disponível para este cenário.</p>
              </div>
            )
          )}

          {/* Fluxo de Caixa */}
          {subTab === 'cashflow' && (
            cashflowData?.rows?.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100" style={{ backgroundColor: '#f0fdf4' }}>
                  <h3 className="font-bold text-sm" style={{ color: BRAND_GREEN }}>Fluxo de Caixa SwimVision</h3>
                  <div className="flex gap-6 mt-1 text-xs text-gray-500">
                    {cashflowData.meta?.burn_rate_medio != null && <span>Burn médio: {fmt(cashflowData.meta.burn_rate_medio)}/mês</span>}
                    {cashflowData.meta?.runway_meses != null && <span>Runway: {cashflowData.meta.runway_meses} meses</span>}
                    {cashflowData.meta?.break_even_mes != null && <span>Break-even: mês {cashflowData.meta.break_even_mes}</span>}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2 text-left text-gray-500">Mês</th>
                        <th className="px-3 py-2 text-right text-gray-500">Recebimentos</th>
                        <th className="px-3 py-2 text-right text-gray-500">Royalties</th>
                        <th className="px-3 py-2 text-right text-gray-500">Azure</th>
                        <th className="px-3 py-2 text-right text-gray-500">Fixo Alocado</th>
                        <th className="px-3 py-2 text-right font-semibold" style={{ color: BRAND_GREEN }}>Saldo Final</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cashflowData.rows.map((r, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-2 font-medium text-gray-700">{r.mes}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{fmt(r.recebimentos)}</td>
                          <td className="px-3 py-2 text-right text-red-400">{fmt(r.pagamento_royalties)}</td>
                          <td className="px-3 py-2 text-right text-red-400">{fmt(r.pagamento_azure)}</td>
                          <td className="px-3 py-2 text-right text-red-400">{fmt(r.fixo_alocado)}</td>
                          <td className="px-3 py-2 text-right font-bold" style={{ color: r.saldo_final >= 0 ? BRAND_GREEN : '#dc2626' }}>{fmt(r.saldo_final)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-400 px-4 py-2">Caixa real do produto. Fixo alocado = R$15K/mês (infra, produto, suporte).</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <p className="text-sm text-gray-500">Nenhum dado de fluxo SwimVision disponível para este cenário.</p>
              </div>
            )
          )}

          {/* Visão Parceiro */}
          {subTab === 'partner' && (
            partnerData?.rows?.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100" style={{ backgroundColor: '#fffbeb' }}>
                  <h3 className="font-bold text-sm" style={{ color: BRAND_GOLD }}>Visão Parceiro (Sem custos internos)</h3>
                  <p className="text-xs text-gray-500">Compartilhável com parceiros e clubes. Não inclui custos operacionais internos.</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2 text-left text-gray-500">Mês</th>
                        <th className="px-3 py-2 text-right text-gray-500">Clubes</th>
                        <th className="px-3 py-2 text-right text-gray-500">Atletas Est.</th>
                        <th className="px-3 py-2 text-right text-gray-500">Vídeos Est.</th>
                        <th className="px-3 py-2 text-right text-gray-500">Rec. Total</th>
                        <th className="px-3 py-2 text-right" style={{ color: BRAND_GREEN }}>Parceiro</th>
                        <th className="px-3 py-2 text-right" style={{ color: BRAND_GOLD }}>Royalty %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partnerData.rows.map((r, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-amber-50'}>
                          <td className="px-3 py-2 font-medium text-gray-700">{r.mes}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{r.clubes ?? 0}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{r.atletas_estimados ?? 0}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{r.videos_estimados ?? 0}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{fmt(r.receita_total)}</td>
                          <td className="px-3 py-2 text-right font-bold" style={{ color: BRAND_GREEN }}>{fmt(r.receita_parceiro)}</td>
                          <td className="px-3 py-2 text-right font-semibold" style={{ color: BRAND_GOLD }}>{r.royalty_pct != null ? `${r.royalty_pct.toFixed(1)}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-400 px-4 py-2">Projeção de receita partilhada com o parceiro Vukz. Atletas = clubes × 50, Vídeos = clubes × 120.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <p className="text-sm text-gray-500">Nenhum dado de visão parceiro disponível para este cenário.</p>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

const AGENT_STORAGE_KEY = 'arenastats_investors_agent_history';

const QUICK_ACTIONS = [
  { label: 'Resumo do investimento', prompt: 'Me dê um resumo executivo do investimento e do estágio atual da empresa.' },
  { label: 'Valuation atual', prompt: 'Qual é o valuation atual da ArenaStats e como ele foi calculado?' },
  { label: 'Runway e burn rate', prompt: 'Qual é o runway atual e o burn rate mensal?' },
  { label: 'Tese de investimento', prompt: 'Qual é a tese de investimento e o diferencial competitivo?' },
  { label: 'Due diligence pendente', prompt: 'Quais itens de due diligence ainda estão pendentes ou em progresso?' },
];

const buildSystemContext = (scenarioKey) => {
  const scenario = (FINANCIAL_SCENARIOS || []).find(s => s.id === scenarioKey) || (FINANCIAL_SCENARIOS || [])[0];
  const inv = INVESTMENT_SCENARIOS?.[0];
  const valuation = CURRENT_VALUATION;
  const capTable = CAP_TABLE;
  const thesis = INVESTOR_THESIS;

  const lines = [
    '=== CONTEXTO ArenaStats — Data Room Investidores ===',
    `Cenário ativo: ${getScenarioDisplayName ? getScenarioDisplayName(scenarioKey) : scenarioKey}`,
  ];

  if (scenario) {
    const tax = FINANCIAL_ASSUMPTIONS?.taxOnRevenue ?? 0.0933;
    const months = (scenario.monthly_revenue || []).slice(0, 12);
    const totalRev = months.reduce((a, v) => a + (v || 0), 0);
    lines.push(`Receita bruta projetada (12m): R$ ${(totalRev / 1000).toFixed(0)}k`);
    lines.push(`MRR mês 1: R$ ${((months[0] || 0) / 1000).toFixed(1)}k | MRR mês 12: R$ ${((months[11] || 0) / 1000).toFixed(1)}k`);
    if (scenario.monthly_expenses?.length) {
      const totalExp = scenario.monthly_expenses.slice(0, 12).reduce((a, v) => a + (v || 0), 0);
      lines.push(`Custos operacionais (12m): R$ ${(totalExp / 1000).toFixed(0)}k`);
    }
  }

  if (valuation) {
    lines.push(`Valuation pré-money: R$ ${valuation.pre_money_brl ? (valuation.pre_money_brl / 1e6).toFixed(2) + 'M' : 'N/D'}`);
    lines.push(`Rodada Seed alvo: R$ ${valuation.target_raise_brl ? (valuation.target_raise_brl / 1e3).toFixed(0) + 'k' : 'N/D'}`);
  }

  if (capTable?.length) {
    lines.push('Cap Table: ' + capTable.map(s => `${s.name} ${s.pct}%`).join(', '));
  }

  if (thesis?.highlights?.length) {
    lines.push('Destaques da tese: ' + thesis.highlights.slice(0, 3).join(' | '));
  }

  lines.push('=== FIM DO CONTEXTO ===');
  return lines.join('\n');
};

const VukzInvestorsAgent = ({ scenarioKey, onClose }) => {
  const storageKey = AGENT_STORAGE_KEY + '_' + (scenarioKey || 'default');
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = React.useRef(null);

  // Persist history whenever messages change
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(messages)); } catch {}
  }, [messages, storageKey]);

  // Reload history when scenario changes
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      setMessages(saved ? JSON.parse(saved) : []);
    } catch { setMessages([]); }
  }, [storageKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (text) => {
    const userMsg = text.trim();
    if (!userMsg) return;
    setInput('');
    const newMessages = [...messages, { role: 'user', content: userMsg, ts: Date.now() }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const systemCtx = buildSystemContext(scenarioKey);
      // Build conversation history for the API (last 10 exchanges)
      const historyForApi = newMessages.slice(-20).map(m => ({ role: m.role, content: m.content }));
      const res = await fetch('/api/governance/ai/investors-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: historyForApi, context: systemCtx, scenario: scenarioKey }),
      });
      if (res.ok) {
        const json = await res.json();
        const reply = json?.data?.reply || json?.reply || 'Sem resposta do agente.';
        setMessages(prev => [...prev, { role: 'assistant', content: reply, ts: Date.now() }]);
      } else {
        throw new Error('HTTP ' + res.status);
      }
    } catch {
      // Offline fallback — context-aware reply
      const lower = userMsg.toLowerCase();
      let reply = 'No momento estou sem conexão com o servidor. ';
      if (lower.includes('valuation') || lower.includes('valor')) {
        const v = CURRENT_VALUATION;
        reply += `Valuation pré-money estimado: R$ ${v?.pre_money_brl ? (v.pre_money_brl / 1e6).toFixed(2) + 'M' : 'N/D'}. Rodada alvo: R$ ${v?.target_raise_brl ? (v.target_raise_brl / 1e3).toFixed(0) + 'k' : 'N/D'}.`;
      } else if (lower.includes('cap table') || lower.includes('soci')) {
        const ct = CAP_TABLE || [];
        reply += 'Cap Table atual: ' + (ct.length ? ct.map(s => `${s.name} ${s.pct}%`).join(', ') : 'dados indisponíveis') + '.';
      } else if (lower.includes('runway') || lower.includes('burn')) {
        reply += 'Consulte a aba Fluxo de Caixa para ver burn rate e runway detalhados por cenário.';
      } else if (lower.includes('due diligence') || lower.includes('diligência')) {
        reply += 'Acesse a aba Due Diligence para ver o status de cada item. Você pode editar o status diretamente nessa aba.';
      } else if (lower.includes('tese') || lower.includes('diferencial')) {
        const th = INVESTOR_THESIS;
        reply += th?.summary ? th.summary : 'Consulte a aba Tese de Dados & IA para o detalhamento completo.';
      } else {
        reply += 'Tente novamente em breve, ou navegue pelas abas do Data Room para acessar as informações diretamente.';
      }
      setMessages(prev => [...prev, { role: 'assistant', content: reply, ts: Date.now(), offline: true }]);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    setMessages([]);
    try { localStorage.removeItem(storageKey); } catch {}
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Agent header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100" style={{ backgroundColor: BRAND_GREEN }}>
        <Bot className="w-5 h-5 text-white" />
        <div className="flex-1">
          <p className="text-white font-bold text-sm">Vukz Investor Agent</p>
          <p className="text-green-200 text-xs">IA treinada no contexto do Data Room · Histórico: {messages.length} mensagens</p>
        </div>
        <button onClick={clearHistory} title="Limpar histórico" className="text-green-200 hover:text-white transition-colors mr-2">
          <RefreshCw className="w-4 h-4" />
        </button>
        <button onClick={onClose} className="text-green-200 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Quick actions */}
      {messages.length === 0 && (
        <div className="px-4 pt-4 pb-2">
          <p className="text-xs text-gray-500 mb-2 font-medium">Perguntas rápidas:</p>
          <div className="flex flex-wrap gap-2">
            {QUICK_ACTIONS.map((qa, i) => (
              <button key={i} onClick={() => send(qa.prompt)}
                className="text-xs px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full text-gray-700 hover:border-green-400 hover:text-green-800 transition-all">
                {qa.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="h-72 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Bot className="w-10 h-10 mb-3" style={{ color: BRAND_GREEN, opacity: 0.3 }} />
            <p className="text-sm text-gray-500">Faça uma pergunta sobre o Data Room, valuation, runway, due diligence ou tese de investimento.</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.role === 'assistant' && (
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: BRAND_GREEN }}>
                <Bot className="w-4 h-4 text-white" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
              m.role === 'user'
                ? 'text-white rounded-br-none'
                : 'bg-gray-50 border border-gray-100 text-gray-800 rounded-bl-none'
            }`} style={m.role === 'user' ? { backgroundColor: BRAND_GREEN } : {}}>
              <p className="leading-relaxed whitespace-pre-wrap">{m.content}</p>
              {m.offline && <p className="text-xs mt-1 opacity-60">· modo offline</p>}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: BRAND_GREEN }}>
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl rounded-bl-none px-3 py-2">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-gray-100">
        <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Pergunte sobre valuation, runway, due diligence..."
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green-400"
            disabled={loading}
          />
          <button type="submit" disabled={loading || !input.trim()}
            className="px-3 py-2 rounded-lg text-white font-bold text-sm disabled:opacity-40 transition-opacity"
            style={{ backgroundColor: BRAND_GREEN }}>
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
};

const GovernanceInvestorsPage = () => {
  const { lang } = useGovernanceLang();
  const t = INVESTORS_LABELS[lang] || INVESTORS_LABELS.pt;
  const [activeTab, setActiveTab] = useState('overview');
  const [scenarioKey, setScenarioKey] = useState(BASELINE_SCENARIO_KEY);
  const [investorSummary, setInvestorSummary] = useState(null);
  const [summarySource, setSummarySource] = useState('empty');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);

  useEffect(() => {
    const loadSummary = async () => {
      setSummaryLoading(true);
      const result = await fetchInvestorSummary(scenarioKey);
      setInvestorSummary(result.data);
      setSummarySource(result.source);
      setSummaryLoading(false);
    };
    loadSummary();
  }, [scenarioKey]);

  const renderTab = () => {
    switch (activeTab) {
      case 'overview': return <OverviewTab scenarioKey={scenarioKey} investorSummary={investorSummary} summarySource={summarySource} onNavigate={setActiveTab} />;
      case 'dre': return <DRETab />;
      case 'cashflow': return <CashflowTab />;
      case 'produtos': return <ProductsTab />;
      case 'cap-table': return <CapTableTab />;
      case 'thesis': return <ThesisTab />;
      case 'use-of-funds': return <UseOfFundsTab />;
      case 'valuation': return <ValuationTab />;
      case 'market': return <MarketTab />;
      case 'due-diligence': return <DueDiligenceTab />;
      case 'financial': return <FinancialSummaryTab />;
      default: return <PlaceholderTab title={t.tabs[activeTab] || activeTab} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
        <div className="flex-1">
          <h1 className="text-gray-900 text-2xl font-extrabold">{t.title}</h1>
          <p className="text-gray-500 mt-1">{t.subtitle}</p>
        </div>
      </div>

      {/* Scenario Selector */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <label className="text-sm font-bold text-gray-900">{t.scenarioLabel}</label>
            {summaryLoading && <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />}
          </div>
          <button
            onClick={() => setAgentOpen(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
              agentOpen ? 'text-white border-transparent' : 'bg-white border-gray-200 text-gray-600 hover:border-green-400'
            }`}
            style={agentOpen ? { backgroundColor: BRAND_GREEN } : {}}
          >
            <Bot className="w-3.5 h-3.5" />
            Vukz Agent
          </button>
        </div>
        <select
          value={scenarioKey}
          onChange={(e) => setScenarioKey(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          disabled={summaryLoading}
        >
          <option value="zero_normal">Normal sem investimento (Baseline)</option>
          <option value="zero_pessimista">Sem investimento — Pessimista</option>
          <option value="zero_otimista">Sem investimento — Otimista</option>
          <option value="seed_250k_normal">Seed 250k — Normal</option>
          <option value="seed_250k_pessimista">Seed 250k — Pessimista</option>
          <option value="seed_250k_otimista">Seed 250k — Otimista</option>
          <option value="seed_500k_normal">Seed 500k — Normal</option>
          <option value="seed_500k_pessimista">Seed 500k — Pessimista</option>
          <option value="seed_500k_otimista">Seed 500k — Otimista</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Todos os dados financeiros (DRE, Fluxo de Caixa, Unit Economics, Runway) serão exibidos para o cenário selecionado.
        </p>
      </div>

      {/* Agent panel — inline */}
      {agentOpen && <VukzInvestorsAgent onClose={() => setAgentOpen(false)} scenarioKey={scenarioKey} />}

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-xl">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{t.tabs[tab.id] || tab.id}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {renderTab()}
    </div>
  );
};

export default GovernanceInvestorsPage;
